/**
 * gltfPack — 把「多檔 glTF」（.gltf JSON + 外部 .bin / 貼圖）無損打包成單一 .glb。
 *
 * 為什麼需要它：後端 uploadGltf 只會存 .gltf 本體，旁邊的 .bin 與貼圖不會上傳，
 * viewer 依相對路徑抓資源時 404。打包成 GLB 後就能直接走既有的 uploadGlb 流程
 * （發佈複製、CDN、AR 都不用改）。
 *
 * 特性：
 * - 不解碼幾何，只做容器轉換：Draco / meshopt / 任何 extension 的 bytes 原樣帶過去。
 * - 支援多個 buffers（合併成一個 BIN chunk）、data: URI、外部檔案、含中文/空格/percent-encoding 的路徑。
 * - 零依賴，瀏覽器與 Node 都能跑（單元測試用 Node）。
 */

const GLB_MAGIC = 0x46546c67; // 'glTF'
const CHUNK_JSON = 0x4e4f534a; // 'JSON'
const CHUNK_BIN = 0x004e4942; // 'BIN\0'
const align4 = (n) => (n + 3) & ~3;

/** 帶 code 的 Error，讓 UI 依 code 決定顯示哪個 modal（OVER_SIZE → 容量過大，其餘 → 缺檔提示）。 */
export const codedError = (code, message) => Object.assign(new Error(message), { code });

/**
 * 把 glTF 相對 URI 正規化成可比對的路徑鍵：解 percent-encoding、統一 /、去 ./ 與重複斜線、
 * 收斂 a/../b → b（開頭的 .. 保留，交給 makeMapResolver 以 .gltf 所在資料夾解析）、NFC。
 */
export function normalizeUriPath(uri) {
  let p = String(uri);
  try {
    p = decodeURIComponent(p);
  } catch (_) {
    /* 不是合法 percent-encoding 就保留原字串 */
  }
  const segs = p.replace(/\\/g, "/").split("/");
  const out = [];
  segs.forEach((seg, i) => {
    if (seg === "." || (seg === "" && i > 0 && i < segs.length - 1)) return;
    if (seg === ".." && out.length && out[out.length - 1] !== "..") {
      out.pop();
      return;
    }
    out.push(seg);
  });
  return out.join("/").normalize("NFC");
}

/**
 * 用 { 相對路徑: bytes } 建立 URI resolver。比對順序：
 * (1) 正規化後的完整相對路徑；
 * (2) 有 baseDir（.gltf 所在資料夾）時，把 URI 接在 baseDir 後解析 ../ 再比對完整路徑
 *     （splitGltfAndResources 對資料夾外的檔案保留完整路徑，所以 ../shared/tex.png 對得到）；
 * (3) 都對不到才退回比對檔名，且只有「同名檔案剛好一個」才用（讓 zip 多包一層資料夾也能對上）。
 * 同名檔案不只一個時不猜，直接丟錯，避免默默打包到錯的貼圖 / .bin。
 */
export function makeMapResolver(fileMap, baseDir = "") {
  const byPath = new Map();
  const byBase = new Map(); // 檔名 → [完整路徑, ...]
  Object.entries(fileMap).forEach(([k, v]) => {
    const np = normalizeUriPath(k);
    byPath.set(np, v);
    const base = np.split("/").pop();
    byBase.set(base, [...(byBase.get(base) || []), np]);
  });
  const dir = baseDir ? normalizeUriPath(baseDir) : "";
  return (uri) => {
    const np = normalizeUriPath(uri);
    if (byPath.has(np)) return byPath.get(np);
    if (dir) {
      const fromDir = normalizeUriPath(`${dir}/${np}`);
      if (byPath.has(fromDir)) return byPath.get(fromDir);
    }
    const candidates = byBase.get(np.split("/").pop()) || [];
    if (candidates.length === 1) return byPath.get(candidates[0]);
    if (candidates.length > 1) {
      throw new Error(
        `glTF 引用的 ${uri} 對不到完整路徑，且有多個同名檔案（${candidates.join("、")}），無法判斷要用哪一個`,
      );
    }
    throw new Error(`glTF 引用的檔案沒有一起提供：${uri}`);
  };
}

function base64ToBytes(b64) {
  if (typeof atob === "function") {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
    return out;
  }
  // eslint-disable-next-line no-undef
  return new Uint8Array(Buffer.from(b64, "base64"));
}

const HEX_PAIR = /^[0-9a-fA-F]{2}$/;

/**
 * 解 data URI 的百分號編碼，直接產生原始 byte。
 * 不能用 decodeURIComponent()：它只接受合法的 UTF-8 序列，遇到 %80、%FF 這類任意
 * 二進位 byte 會丟 URIError；就算沒丟，結果再經過 TextEncoder 也會被重新編成
 * UTF-8，byte 數與原始資料對不起來。
 */
function percentDecodeToBytes(text) {
  const encoder = new TextEncoder();
  const isEscape = (i) => text[i] === "%" && HEX_PAIR.test(text.slice(i + 1, i + 3));
  const chunks = [];
  let total = 0;
  const push = (bytes) => {
    if (!bytes.length) return;
    chunks.push(bytes);
    total += bytes.length;
  };
  let i = 0;
  while (i < text.length) {
    if (isEscape(i)) {
      const raw = [];
      while (isEscape(i)) {
        raw.push(parseInt(text.slice(i + 1, i + 3), 16));
        i += 3;
      }
      push(Uint8Array.from(raw));
    } else {
      let j = i;
      while (j < text.length && !isEscape(j)) j += 1;
      // 沒被編碼的部分照 UTF-8 走（合法的 data URI 這段只會有 ASCII）
      push(encoder.encode(text.slice(i, j)));
      i = j;
    }
  }
  const out = new Uint8Array(total);
  let offset = 0;
  chunks.forEach((c) => {
    out.set(c, offset);
    offset += c.length;
  });
  return out;
}

function decodeDataUri(uri) {
  // media type 後面可以帶參數（例如 ;charset=utf-8），所以用非貪婪比對，
  // 讓 ;base64 以第一個能讓整串成立的位置為準。
  const m = /^data:([^,]*?)(;base64)?,([\s\S]*)$/.exec(uri);
  if (!m) throw new Error("data URI 格式錯誤");
  const mime = m[1].split(";")[0].trim() || undefined;
  const bytes = m[2] ? base64ToBytes(m[3]) : percentDecodeToBytes(m[3]);
  return { bytes, mime };
}

async function loadUri(uri, resolve) {
  if (uri.startsWith("data:")) return decodeDataUri(uri);
  const r = await resolve(uri);
  const bytes = r instanceof Uint8Array ? r : new Uint8Array(r);
  return { bytes, mime: undefined };
}

/** 從檔頭判斷圖片 MIME，判不出來再看副檔名。 */
export function sniffImageMime(bytes, uri = "") {
  const b = bytes;
  if (b.length >= 4 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) {
    return "image/png";
  }
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "image/jpeg";
  if (b.length >= 12 && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) {
    return "image/webp";
  }
  if (b.length >= 4 && b[0] === 0xab && b[1] === 0x4b && b[2] === 0x54 && b[3] === 0x58) {
    return "image/ktx2";
  }
  const ext = normalizeUriPath(uri).split(".").pop().toLowerCase();
  const byExt = {
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    webp: "image/webp",
    ktx2: "image/ktx2",
  };
  return byExt[ext] || "application/octet-stream";
}

/**
 * 核心：glTF JSON 文字 + resolver → GLB bytes。
 * @param {string} gltfText .gltf 檔內容
 * @param {(uri: string) => (Promise<ArrayBuffer|Uint8Array>|ArrayBuffer|Uint8Array)} resolve 取外部 URI 的 bytes
 * @param {{ maxBytes?: number }} [opts] 輸出上限：BIN 每次累加前、以及配置最終輸出前都會檢查，
 *   超過就丟 code=OVER_SIZE，不會先做出超大的 GLB 再檢查（防止大量 images / buffers 重複引用把小輸入放大）
 * @returns {Promise<Uint8Array>}
 */
export async function packGltfToGlb(gltfText, resolve, { maxBytes = Infinity } = {}) {
  const gltf = JSON.parse(gltfText);
  if (!gltf.asset || String(gltf.asset.version).split(".")[0] !== "2") {
    throw new Error("只支援 glTF 2.0");
  }

  const chunks = [];
  let offset = 0;
  const push = (u8) => {
    const start = offset;
    if (offset + align4(u8.byteLength) > maxBytes) {
      throw codedError(
        "OVER_SIZE",
        `打包中的 GLB 已超過上限 ${maxBytes} bytes（BIN 累計 ${offset + u8.byteLength} bytes）`,
      );
    }
    chunks.push(u8);
    offset += u8.byteLength;
    const pad = align4(offset) - offset;
    if (pad) {
      chunks.push(new Uint8Array(pad));
      offset += pad;
    }
    return start;
  };

  // 1) 所有 buffers 合併成一個 BIN chunk，記下每個 buffer 的起點
  const buffers = gltf.buffers || [];
  const bufferOffsets = [];
  for (let i = 0; i < buffers.length; i += 1) {
    const b = buffers[i];
    if (b.uri === undefined) {
      throw new Error(`buffers[${i}] 沒有 uri，輸入看起來已經是 GLB`);
    }
    // eslint-disable-next-line no-await-in-loop
    const { bytes } = await loadUri(b.uri, resolve);
    const declared = b.byteLength;
    if (declared !== undefined && bytes.byteLength < declared) {
      throw new Error(
        `buffers[${i}]（${b.uri}）只有 ${bytes.byteLength} bytes，但 glTF 宣告 ${declared}`,
      );
    }
    bufferOffsets.push(push(declared !== undefined ? bytes.subarray(0, declared) : bytes));
  }
  (gltf.bufferViews || []).forEach((bv) => {
    const base = bufferOffsets[bv.buffer || 0];
    if (base === undefined) throw new Error(`bufferView 引用不存在的 buffer ${bv.buffer}`);
    bv.byteOffset = (bv.byteOffset || 0) + base;
    bv.buffer = 0;
    const mo = bv.extensions && bv.extensions.EXT_meshopt_compression;
    if (mo && mo.buffer !== undefined) {
      const mbase = bufferOffsets[mo.buffer];
      if (mbase === undefined) {
        throw new Error(`EXT_meshopt_compression 引用不存在的 buffer ${mo.buffer}`);
      }
      mo.byteOffset = (mo.byteOffset || 0) + mbase;
      mo.buffer = 0;
    }
  });

  // 2) 有 uri 的圖片改成 bufferView 內嵌；同一個 uri 被多個 images 引用時只放一份、共用 bufferView
  const images = gltf.images || [];
  const imageViews = new Map(); // uri 鍵 → { bufferView, mime }
  for (let i = 0; i < images.length; i += 1) {
    const img = images[i];
    if (img.uri !== undefined) {
      const key = img.uri.startsWith("data:") ? img.uri : normalizeUriPath(img.uri);
      let hit = imageViews.get(key);
      if (!hit) {
        // eslint-disable-next-line no-await-in-loop
        const { bytes, mime } = await loadUri(img.uri, resolve);
        const start = push(bytes);
        gltf.bufferViews = gltf.bufferViews || [];
        gltf.bufferViews.push({ buffer: 0, byteOffset: start, byteLength: bytes.byteLength });
        hit = { bufferView: gltf.bufferViews.length - 1, mime: mime || sniffImageMime(bytes, img.uri) };
        imageViews.set(key, hit);
      }
      img.bufferView = hit.bufferView;
      img.mimeType = img.mimeType || hit.mime;
      delete img.uri;
    }
  }

  // 3) 單一 buffer
  gltf.buffers = offset > 0 ? [{ byteLength: offset }] : [];

  // 4) 組 GLB：12 bytes header + JSON chunk（空白補齊到 4 的倍數）+ BIN chunk
  let json = new TextEncoder().encode(JSON.stringify(gltf));
  const jpad = align4(json.byteLength) - json.byteLength;
  if (jpad) {
    const padded = new Uint8Array(json.byteLength + jpad);
    padded.set(json);
    padded.fill(0x20, json.byteLength);
    json = padded;
  }
  const binLen = offset;
  const total = 12 + 8 + json.byteLength + (binLen ? 8 + binLen : 0);
  if (total > maxBytes) {
    throw codedError("OVER_SIZE", `打包後的 GLB ${total} bytes 超過上限 ${maxBytes} bytes`);
  }
  const out = new Uint8Array(total);
  const dv = new DataView(out.buffer);
  dv.setUint32(0, GLB_MAGIC, true);
  dv.setUint32(4, 2, true);
  dv.setUint32(8, total, true);
  dv.setUint32(12, json.byteLength, true);
  dv.setUint32(16, CHUNK_JSON, true);
  out.set(json, 20);
  if (binLen) {
    let p = 20 + json.byteLength;
    dv.setUint32(p, binLen, true);
    dv.setUint32(p + 4, CHUNK_BIN, true);
    p += 8;
    chunks.forEach((c) => {
      out.set(c, p);
      p += c.byteLength;
    });
  }
  return out;
}

/**
 * 從一批 { path, bytes } 找出唯一的 .gltf，並把其餘檔案整理成相對於該 .gltf 所在資料夾的 map；
 * 不在該資料夾底下的檔案保留完整路徑（配合 makeMapResolver 的 baseDir 解析 ../）。
 * 另回傳 dir（.gltf 所在資料夾，正規化後、無結尾斜線；在根目錄時為空字串）。
 */
export function splitGltfAndResources(entries) {
  const gltfs = entries.filter((e) => /\.gltf$/i.test(e.path));
  if (gltfs.length !== 1) {
    throw new Error(`需要剛好一個 .gltf 檔，但找到 ${gltfs.length} 個`);
  }
  const gltf = gltfs[0];
  const dir = normalizeUriPath(gltf.path).split("/").slice(0, -1).join("/");
  const resources = {};
  entries.forEach((e) => {
    if (e === gltf) return;
    let rel = normalizeUriPath(e.path);
    if (dir && rel.startsWith(`${dir}/`)) rel = rel.slice(dir.length + 1);
    resources[rel] = e.bytes;
  });
  return { gltf, resources, dir };
}

/** 瀏覽器用：把 File 物件（多選 / 拖曳資料夾）讀成 { path, bytes }。 */
export async function collectGltfEntries(files) {
  const list = Array.from(files);
  const bytes = await Promise.all(list.map((f) => f.arrayBuffer()));
  return list.map((f, i) => ({
    path: (f.path || f.webkitRelativePath || f.name || "").replace(/^\/+/, ""),
    bytes: new Uint8Array(bytes[i]),
  }));
}

/**
 * { path, bytes }[] → { bytes, name, stats }。
 * 回傳的 name 是 .gltf 檔名換成 .glb，後端會拿它當 glbName；bytes 是 GLB 內容（Uint8Array，
 * 不包成 Blob / File，讓 Node 下的測試能直接驗，由呼叫端再組 File）。
 */
export async function packEntriesToGlb(entries, opts) {
  const { gltf, resources, dir } = splitGltfAndResources(entries);
  const glb = await packGltfToGlb(
    new TextDecoder().decode(gltf.bytes),
    makeMapResolver(resources, dir),
    opts,
  );
  const baseName = normalizeUriPath(gltf.path)
    .split("/")
    .pop()
    .replace(/\.gltf$/i, "");
  return {
    bytes: glb,
    name: `${baseName}.glb`,
    stats: {
      inputFiles: entries.length,
      inputBytes: entries.reduce((s, e) => s + e.bytes.byteLength, 0),
      glbBytes: glb.byteLength,
    },
  };
}

/** 瀏覽器用一站式：File[]（多選 / 拖曳資料夾）→ { bytes, name, stats }。opts 同 packGltfToGlb。 */
export async function packFilesToGlb(files, opts) {
  return packEntriesToGlb(await collectGltfEntries(files), opts);
}

/** 判斷一批 File 是不是「多檔 glTF」上傳（剛好一個 .gltf，其餘是它的資源）。 */
export function looksLikeMultiFileGltf(files) {
  const list = Array.from(files);
  return list.length >= 1 && list.filter((f) => /\.gltf$/i.test(f.name)).length === 1;
}
